import type { CollectionReference, DocumentData } from 'firebase/firestore'
import {
  collection,
  doc,
  serverTimestamp,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { modelItemStream, modelListStream } from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { buildUnsupportedConverter } from '../shared/noops'
import type { FirestorePromptPostAssignmentType } from './schema'
import { schema } from './schema'
import { PromptPostAssignmentType } from '../../models/PromptPostAssignmentType'
import { updateDocWithError } from '../../firestore-mobx/fetch'
import { type PostAssignmentPromptType } from './types'

export * from './schema'

const unsupported =
  buildUnsupportedConverter<FirestorePromptPostAssignmentType>()

const readConverter: FirestoreDataConverter<FirestorePromptPostAssignmentType> =
  {
    toFirestore: unsupported.toFirestore,
    fromFirestore: (snapshot: QueryDocumentSnapshot) => {
      const data = snapshot.data({ serverTimestamps: 'estimate' })
      return schema.parse(data)
    },
  }

const getReadCollectionRef = (
  firestore: Firestore
): CollectionReference<FirestorePromptPostAssignmentType, DocumentData> => {
  return collection(
    firestore,
    'prompt',
    'post_assignment_prompt',
    'type'
  ).withConverter(readConverter)
}

export const getPromptPostAssignmentTypes = (
  repository: FirebaseRepository
) => {
  const ref = getReadCollectionRef(repository.firestore)
  return modelListStream(repository, ref, PromptPostAssignmentType)
}

export const getPromptPostAssignmentType = (
  repository: FirebaseRepository,
  { type }: { type: PostAssignmentPromptType }
) => {
  const col = getReadCollectionRef(repository.firestore)
  const docRef = doc(col, type)
  return modelItemStream(repository, docRef, PromptPostAssignmentType)
}

export const updatePromptPostAssignmentType = async (
  repository: FirebaseRepository,
  {
    title,
    description,
    postAssignmentType,
  }: Pick<FirestorePromptPostAssignmentType, 'title' | 'description'> & {
    postAssignmentType: PostAssignmentPromptType
  }
) => {
  const ref = getReadCollectionRef(repository.firestore)
  const docRef = doc(ref, postAssignmentType).withConverter(null)
  return await updateDocWithError(
    docRef,
    {
      title,
      description,
      updatedAt: serverTimestamp(),
    },
    'UpdatePromptPostAssignmentTypeError'
  )
}
